import { Link, useNavigate } from "react-router-dom";
import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Briefcase, MapPin } from "lucide-react";
import SiteFooter from "../components/SiteFooter";
import { PAYS_ELIGIBLES, normalisePays } from "../components/form/PaysEligibles";

const apparition = (delai = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: delai, ease: "easeOut" },
});

/* Bloc 2 — les 3 rubriques : médaillons iconographiques (titre + icône adaptée) */
const RUBRIQUES = [
  {
    titre: "Adresse domiciliation en France",
    icone: MapPin,
    classe: "icone-france",
    iconeCouleur: "",
    couche: "bg-gradient-to-b from-[#60a5fa] to-[#2563eb] shadow-[0_24px_44px_-14px_rgba(37,99,235,0.5)]",
    medaillon: "bg-gradient-to-b from-[#4c6ef5] to-[#1e2f55] shadow-[inset_0_3px_8px_rgba(255,255,255,0.4),0_18px_32px_-10px_rgba(43,70,196,0.6)]",
  },
  {
    titre: "N° Travailleur Indépendant en France",
    icone: BadgeCheck,
    classe: "icone-valide",
    iconeCouleur: "",
    couche: "bg-gradient-to-b from-[#4c6ef5] to-[#1e3a8a] shadow-[0_24px_44px_-14px_rgba(30,58,138,0.5)]",
    medaillon: "bg-gradient-to-b from-[#4c6ef5] to-[#1e2f55] shadow-[inset_0_3px_8px_rgba(255,255,255,0.4),0_18px_32px_-10px_rgba(43,70,196,0.6)]",
  },
  {
    titre: "Travailler légalement en France",
    icone: Briefcase,
    classe: "",
    iconeCouleur: "text-[#fbbf24]",
    couche: "bg-gradient-to-b from-[#38bdf8] to-[#0284c7] shadow-[0_24px_44px_-14px_rgba(2,132,199,0.5)]",
    medaillon: "bg-gradient-to-b from-[#4c6ef5] to-[#1e2f55] shadow-[inset_0_3px_8px_rgba(255,255,255,0.4),0_18px_32px_-10px_rgba(43,70,196,0.6)]",
  },
];

/* Bloc 3 — le schéma des étapes (titres uniquement, sans descriptions) */
const ETAPES = [
  { numero: "01", titre: "Pré-enregistrement souscription" },
  { numero: "02", titre: "Validation souscription" },
  { numero: "03", titre: "Dépôt dossier administratif" },
  { numero: "04", titre: "Vérification conformité" },
  { numero: "05", titre: "N° TI Français délivré" },
  { numero: "06", titre: "Statut Travailleur Français" },
];

/* Bloc 5 — conditions d'éligibilité (même style que le schéma des étapes) */
const CONDITIONS = [
  { numero: "01", titre: "Être âgé(e) de 18 ans +" },
  { numero: "02", titre: "Avoir 2 pièces d'identités valides" },
  { numero: "03", titre: "Résider dans un pays éligible" },
  { numero: "04", titre: "Proposer une compétence" },
  { numero: "05", titre: "Parler couramment le Français" },
  { numero: "06", titre: "Savoir lire et écrire en autonomie" },
];

/* Une couleur de médaillon par condition (dégradés assortis à la charte) */
const COULEURS_CONDITIONS = [
  "bg-gradient-to-b from-[#3d5ae0] to-[#2b46c4] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(43,70,196,0.65)]",
  "bg-gradient-to-b from-[#fbbf24] to-[#f59e0b] shadow-[inset_0_2px_6px_rgba(255,255,255,0.5),0_14px_28px_-6px_rgba(245,158,11,0.65)]",
  "bg-gradient-to-b from-[#14b8a6] to-[#0d9488] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(13,148,136,0.6)]",
  "bg-gradient-to-b from-emerald-500 to-emerald-600 shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(5,150,105,0.6)]",
  "bg-gradient-to-b from-[#e0312e] to-[#b5271f] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(224,49,46,0.6)]",
  "bg-gradient-to-b from-[#7c3aed] to-[#6526c4] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(124,58,237,0.6)]",
];

/* Bloc 4 — les drapeaux des pays concernés. Toutes les images sont servies par flagcdn.com
   (codes ISO) : aucune dépendance à l'ancien site WordPress, donc plus jamais de badge cassé. */
const PAYS = [
  { nom: "Côte d'Ivoire", image: "https://flagcdn.com/w160/ci.png" },
  { nom: "Togo", image: "https://flagcdn.com/w160/tg.png" },
  { nom: "Bénin", image: "https://flagcdn.com/w160/bj.png" },
  { nom: "Sénégal", image: "https://flagcdn.com/w160/sn.png" },
  { nom: "Congo", image: "https://flagcdn.com/w160/cg.png" },
  { nom: "Cameroun", image: "https://flagcdn.com/w160/cm.png" },
  { nom: "Burundi", image: "https://flagcdn.com/w160/bi.png" },
  { nom: "Gabon", image: "https://flagcdn.com/w160/ga.png" },
  { nom: "Guinée", image: "https://flagcdn.com/w160/gn.png" },
  { nom: "RD Congo", image: "https://flagcdn.com/w160/cd.png" },
  { nom: "Madagascar", image: "https://flagcdn.com/w160/mg.png" },
  { nom: "Centrafrique", image: "https://flagcdn.com/w160/cf.png" },
];

/* Bande supplémentaire : tous les AUTRES pays éligibles au service (liste officielle
   PAYS_ELIGIBLES du formulaire de Pré-enregistrement) absents de la première bande.
   Drapeaux servies par flagcdn.com (codes ISO). */
const CODES_ISO_PAYS = {
  "benin": "bj",
  "burkina faso": "bf",
  "burundi": "bi",
  "cameroun": "cm",
  "comores": "km",
  "congo republique du": "cg",
  "cote d ivoire": "ci",
  "djibouti": "dj",
  "gabon": "ga",
  "guinee": "gn",
  "guinee equatoriale": "gq",
  "madagascar": "mg",
  "mali": "ml",
  "niger": "ne",
  "republique centrafricaine": "cf",
  "republique democratique du congo": "cd",
  "senegal": "sn",
  "seychelles": "sc",
  "tchad": "td",
  "togo": "tg",
};

/* Équivalences : pays de la liste officielle déjà représentés dans la première bande sous un autre nom */
const DEJA_DANS_PREMIERE_BAND = {
  "congo republique du": "congo",
  "republique democratique du congo": "rd congo",
  "republique centrafricaine": "centrafrique",
};

/* Noms affichés plus compacts pour les intitulés officiels les plus longs */
const NOMS_AFFICHES = {
  "République démocratique du Congo": "RD Congo",
  "République centrafricaine": "Centrafrique",
};

const PAYS_SUPPLEMENTAIRES = PAYS_ELIGIBLES.filter((nom) => {
  const cle = normalisePays(nom);
  return !PAYS.some((p) => (DEJA_DANS_PREMIERE_BAND[cle] || cle) === normalisePays(p.nom));
}).map((nom) => ({
  nom,
  nomAffiche: NOMS_AFFICHES[nom] || nom,
  slug: normalisePays(nom).replace(/ /g, "-"),
  image: `https://flagcdn.com/w160/${CODES_ISO_PAYS[normalisePays(nom)]}.png`,
}));

export default function AccueilPage() {
  const navigate = useNavigate();
  /* Accès DISCRET à l'espace interne : double-clic (ou double-tap) sur la vignette du drapeau UE.
     Aucun indice visuel : pas de curseur particulier, pas d'infobulle — l'entrée reste secrète. */
  const dernierClicUE = useRef(0);
  const surVignetteUE = () => {
    const maintenant = Date.now();
    if (maintenant - dernierClicUE.current < 450) {
      dernierClicUE.current = 0;
      navigate("/interne");
      return;
    }
    dernierClicUE.current = maintenant;
  };
  return (
    <div className="min-h-screen bg-[#f8f8f8] text-[#151515]">
      {/* En-tête : identique aux autres pages publiques (bandeau logo sticky) */}
      <header
        data-testid="accueil-entete"
        className="sticky top-0 z-30 bg-white shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]"
      >
        <div className="tricolor-bar" />
        <div className="mx-auto flex max-w-3xl flex-col items-center px-6 pb-3 pt-4 text-center">
          <Link to="/" data-testid="accueil-logo" className="brand-logo text-[26px] sm:text-[30px]">
            <span className="text-[#2b46c4]">PASS</span>
            <span className="text-[#101a33]">PRO</span>
            <span className="text-[#e0312e]">FRANCE</span>
          </Link>
          <p className="mt-1 text-[10.5px] font-medium uppercase tracking-[0.3em] text-slate-400" data-testid="accueil-entete-tagline">
            France Administrative
          </p>
        </div>
      </header>

      <main>
        {/* Bloc 1 — bandeau bleu nuit « officiel » compact : sceaux France/UE + titre sur une ligne */}
        <section className="relative overflow-hidden bg-[#101a33] pb-10 pt-6 text-center sm:pb-12 sm:pt-8" data-testid="accueil-presentation">
          <div aria-hidden="true" className="absolute inset-x-0 top-0 flex h-1.5">
            <span className="w-1/3 bg-[#2b46c4]" />
            <span className="w-1/3 bg-white" />
            <span className="w-1/3 bg-[#e0312e]" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
            <motion.div {...apparition()} className="flex items-center justify-center -space-x-2">
              <motion.img
                src="https://flagcdn.com/w160/fr.png"
                alt="Drapeau : France"
                loading="lazy"
                data-testid="accueil-vignette-france"
                className="h-9 w-9 rounded-full object-cover shadow-md shadow-black/30 ring-2 ring-white/25 sm:h-11 sm:w-11"
                animate={{ y: [0, -4, 0], rotate: -8 }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.img
                src="https://flagcdn.com/w160/eu.png"
                alt="Drapeau : Union Européenne"
                loading="lazy"
                data-testid="accueil-vignette-ue"
                onClick={surVignetteUE}
                style={{ touchAction: "manipulation" }}
                className="z-10 h-9 w-9 rounded-full object-cover shadow-md shadow-black/30 ring-2 ring-white/25 sm:h-11 sm:w-11"
                animate={{ y: [0, -4, 0], rotate: 8 }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
              />
            </motion.div>
            <motion.p
              {...apparition(0.15)}
              data-testid="accueil-presentation-phrase"
              className="mx-auto mt-6 text-balance text-[20px] font-bold uppercase leading-tight tracking-tight text-white sm:text-[22px] xl:whitespace-nowrap xl:text-[26px]"
            >
              <span className="[word-spacing:0.18em]">Identification professionnelle légale en France,{" "}</span>
              <span className="font-afrique normal-case text-[1.15em] leading-none text-[#5c7cfa]">
                depuis l'Afrique
              </span>
            </motion.p>
            <motion.div {...apparition(0.25)} aria-hidden="true" className="mx-auto mt-6 flex w-44 justify-center">
              <span className="h-1.5 w-1/3 rounded-l-full bg-[#2b46c4]" />
              <span className="h-1.5 w-1/3 bg-white" />
              <span className="h-1.5 w-1/3 rounded-r-full bg-[#e0312e]" />
            </motion.div>
          </div>
        </section>

        {/* Bloc 2 — 3 rubriques : fracture fluide en vagues + fond vivant aux halos tricolores */}
        <section className="relative overflow-hidden bg-gradient-to-b from-[#eef1ff] via-[#f5f8ff] to-white pb-16 pt-24 sm:pt-28" data-testid="accueil-rubriques">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[74px]">
            <svg className="h-full w-[110%] -ml-[5%]" viewBox="0 0 1584 74" preserveAspectRatio="none">
              <path
                className="vague vague-fond"
                d="M0,0 H1584 V22 C1300,66 1056,8 792,34 C516,60 253,14 0,30 Z"
                fill="#fadada"
              />
              <path
                className="vague vague-devant"
                d="M0,0 H1584 V38 C1266,12 1045,64 792,42 C528,20 275,58 0,26 Z"
                fill="#ffffff"
              />
            </svg>
          </div>
          <div aria-hidden="true" className="halo halo-bleu" />
          <div aria-hidden="true" className="halo halo-rouge" />
          <div aria-hidden="true" className="halo halo-ambre" />
          <svg aria-hidden="true" width="0" height="0" className="absolute">
            <defs>
              <linearGradient id="degrade-france" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2b46c4" />
                <stop offset="50%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#e0312e" />
              </linearGradient>
            </defs>
          </svg>
          <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-6 px-3 sm:px-6 md:grid-cols-3 md:gap-7">
            {RUBRIQUES.map((r, i) => (
              <motion.article
                key={r.titre}
                {...apparition(0.1 * i)}
                className="group relative transition-transform duration-300 hover:-translate-y-2"
                data-testid={`accueil-rubrique-${i + 1}`}
              >
                <div
                  aria-hidden="true"
                  className={`absolute inset-x-0 top-0 -bottom-3.5 rounded-2xl ${r.couche}`}
                />
                <div className="relative z-10 rounded-2xl bg-white p-2.5 ring-1 ring-slate-100">
                  <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#f0f4ff] to-[#e2eafc]">
                    <span aria-hidden="true" className="absolute h-36 w-36 rounded-full border-2 border-[#2b46c4]/10" />
                    <span aria-hidden="true" className="absolute h-48 w-48 rounded-full border border-[#2b46c4]/5" />
                    <motion.span
                      className={`relative z-10 inline-flex h-24 w-24 items-center justify-center rounded-full text-white ${r.medaillon}`}
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 * i }}
                    >
                      <r.icone className={`${r.classe} ${r.iconeCouleur} h-11 w-11 transition-transform duration-300 group-hover:scale-110`} strokeWidth={1.8} />
                    </motion.span>
                  </div>
                  <h2 className="px-2 pb-1.5 pt-3.5 text-center text-[16.5px] font-bold leading-snug text-[#1e2f55]">
                    {r.titre}
                  </h2>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        {/* Conditions d'éligibilité : remonté juste après le bloc 2, même style que le schéma des étapes */}
        <section className="relative overflow-hidden bg-white pb-16 pt-14 sm:pb-20 sm:pt-16" data-testid="accueil-conditions">
          <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
            <motion.div {...apparition()}>
              <div aria-hidden="true" className="mx-auto flex w-28 justify-center">
                <span className="h-1.5 w-1/3 rounded-l-full bg-[#2b46c4]" />
                <span className="h-1.5 w-1/3 bg-slate-200" />
                <span className="h-1.5 w-1/3 rounded-r-full bg-[#e0312e]" />
              </div>
              <h2 className="mt-5 text-xl font-extrabold leading-tight tracking-tight text-[#101a33] sm:text-3xl" data-testid="accueil-conditions-titre">
                Conditions{" "}
                <span className="font-afrique text-[1.15em] leading-none text-[#2b46c4]">d'éligibilité</span>
              </h2>
              <div aria-hidden="true" className="mx-auto mt-5 flex w-40 justify-center">
                <span className="h-1.5 w-1/3 rounded-l-full bg-[#2b46c4]" />
                <span className="h-1.5 w-1/3 bg-slate-200" />
                <span className="h-1.5 w-1/3 rounded-r-full bg-[#e0312e]" />
              </div>
            </motion.div>
          </div>
          <div className="relative mx-auto mt-14 grid max-w-6xl grid-cols-1 gap-12 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3 lg:gap-8">
            {CONDITIONS.map((c, i) => (
              <motion.div
                key={c.numero}
                {...apparition(0.1 * i)}
                className="relative text-center"
                data-testid={`accueil-condition-${i + 1}`}
              >
                <span
                  className={`relative z-10 inline-flex h-14 w-14 items-center justify-center rounded-full text-[15px] font-black text-white transition-transform duration-200 hover:scale-110 ${COULEURS_CONDITIONS[i % COULEURS_CONDITIONS.length]}`}
                >
                  {c.numero}
                </span>
                <h3 className="mt-5 text-[16px] font-bold leading-snug text-[#1e2f55]">{c.titre}</h3>
              </motion.div>
            ))}
          </div>
          <motion.div {...apparition(0.3)} className="mt-12 text-center">
            <h3
              className="font-afrique text-2xl leading-none text-[#2b46c4] sm:text-3xl"
              data-testid="accueil-questions-titre"
            >
              Des questions ?
            </h3>
            <Link
              to="/faq"
              data-testid="accueil-conditions-faq-bouton"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#2b46c4] px-6 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white shadow-lg shadow-[#2b46c4]/30 transition-all duration-200 hover:bg-[#2036a1] active:scale-95"
            >
              Consulter la FAQ
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </section>

        {/* Bloc 4 — défilement continu des drapeaux (fond blanc, sans rupture) */}
        <section className="overflow-hidden bg-white py-16 sm:py-20" data-testid="accueil-pays">
          <div
            className="flex w-max animate-[defilement_28s_linear_infinite] hover:[animation-play-state:paused]"
            data-testid="accueil-pays-defilement"
          >
            {[false, true].map((clone) => (
              <div key={String(clone)} aria-hidden={clone || undefined} className="flex shrink-0 items-center">
                {PAYS.map((p) => (
                  <div
                    key={p.nom}
                    className="mx-7 flex w-36 shrink-0 flex-col items-center gap-2.5 sm:mx-10"
                    data-testid={clone ? undefined : `accueil-pays-${p.nom.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white ring-2 ring-slate-100 shadow-[0_14px_28px_-10px_rgba(15,23,42,0.45),inset_0_2px_6px_rgba(255,255,255,0.7)] sm:h-[72px] sm:w-[72px]">
                      <img
                        src={p.image}
                        alt={`Drapeau : ${p.nom}`}
                        loading="lazy"
                        className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200/70 sm:h-[52px] sm:w-[52px]"
                      />
                    </span>
                    <p className="text-[13.5px] font-bold text-[#1e2f55]">{p.nom}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* Bloc 3 — titre + schéma des étapes */}
        <section className="bg-white pb-20 pt-14 sm:pb-24 sm:pt-16" data-testid="accueil-etapes">
          <motion.div {...apparition()} className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <div aria-hidden="true" className="mx-auto flex w-28 justify-center">
              <span className="h-1.5 w-1/3 rounded-l-full bg-[#2b46c4]" />
              <span className="h-1.5 w-1/3 bg-slate-200" />
              <span className="h-1.5 w-1/3 rounded-r-full bg-[#e0312e]" />
            </div>
            <h2 className="mt-5 text-xl font-extrabold leading-tight tracking-tight text-[#101a33] sm:text-3xl" data-testid="accueil-etapes-titre">
              Étapes de la procédure
              <span className="mt-1.5 block font-afrique text-[1.15em] leading-none text-[#2b46c4]">depuis l'Afrique</span>
            </h2>
            <div aria-hidden="true" className="mx-auto mt-5 flex w-40 justify-center">
              <span className="h-1.5 w-1/3 rounded-l-full bg-[#2b46c4]" />
              <span className="h-1.5 w-1/3 bg-slate-200" />
              <span className="h-1.5 w-1/3 rounded-r-full bg-[#e0312e]" />
            </div>
          </motion.div>
          <div className="relative mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-12 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-6 lg:gap-5">
              <div aria-hidden="true" className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-[#2b46c4] via-amber-300 to-emerald-500 lg:block" />
              {ETAPES.map((e, i) => (
                <motion.div
                  key={e.numero}
                  {...apparition(0.1 * i)}
                  className="relative text-center"
                  data-testid={`accueil-etape-${i + 1}`}
                >
                  <span
                    className={`relative z-10 inline-flex h-14 w-14 items-center justify-center rounded-full text-[15px] font-black text-white transition-transform duration-200 hover:scale-110 ${
                      i === 0
                        ? "bg-gradient-to-b from-[#3d5ae0] to-[#2b46c4] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(43,70,196,0.65)]"
                        : i === 1
                          ? "bg-gradient-to-b from-[#2c4270] to-[#1e2f55] shadow-[inset_0_2px_6px_rgba(255,255,255,0.35),0_14px_28px_-6px_rgba(30,47,85,0.6)]"
                          : i === 2
                            ? "bg-gradient-to-b from-[#fbbf24] to-[#f59e0b] shadow-[inset_0_2px_6px_rgba(255,255,255,0.5),0_14px_28px_-6px_rgba(245,158,11,0.65)]"
                            : i === 3
                              ? "bg-gradient-to-b from-[#ff5a52] to-[#e0312e] shadow-[inset_0_2px_6px_rgba(255,255,255,0.5),0_14px_28px_-6px_rgba(224,49,46,0.6)]"
                              : i === 4
                                ? "bg-gradient-to-b from-[#14b8a6] to-[#0d9488] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(13,148,136,0.6)]"
                                : "bg-gradient-to-b from-emerald-500 to-emerald-600 shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),0_14px_28px_-6px_rgba(5,150,105,0.6)]"
                    }`}
                  >
                    {e.numero}
                  </span>
                  <h2 className="mt-5 text-[16px] font-bold leading-snug text-[#1e2f55]">{e.titre}</h2>
                </motion.div>
              ))}
          </div>
          <motion.div {...apparition(0.3)} className="mt-12 text-center">
            <Link
              to="/pre-enregistrement"
              data-testid="accueil-etape-pre-enregistrement-bouton"
              className="inline-flex items-center gap-2 rounded-full bg-[#2b46c4] px-6 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white shadow-lg shadow-[#2b46c4]/30 transition-all duration-200 hover:bg-[#2036a1] active:scale-95"
            >
              Démarrer pré-enregistrement
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </section>

        {/* Bloc de valeur — phrase + souscription en ligne + FAQ */}
        <section className="relative overflow-hidden bg-[#101a33] py-16 sm:py-20" data-testid="accueil-bandeau-valeur">
          <div aria-hidden="true" className="absolute inset-x-0 top-0 flex h-1.5">
            <span className="w-1/3 bg-[#2b46c4]" />
            <span className="w-1/3 bg-white" />
            <span className="w-1/3 bg-[#e0312e]" />
          </div>
          <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
            <motion.p
              {...apparition()}
              data-testid="accueil-bandeau-texte"
              className="text-lg font-bold leading-snug tracking-tight text-white sm:text-2xl"
            >
              Un statut{" "}
              <span className="font-afrique px-2 text-[1.15em] leading-none text-amber-400">100% légal</span> de
              travailleur indépendant en France, et un accès direct à de nombreuses opportunités professionnelles en
              France et dans toute l'UE,{" "}
              <span className="font-afrique pl-1.5 text-[1.15em] leading-none text-[#5c7cfa]">depuis l'Afrique</span>
            </motion.p>
            <motion.div {...apparition(0.15)} className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/agences"
                data-testid="accueil-bandeau-agences-bouton"
                className="inline-flex items-center gap-2 rounded-full bg-[#2b46c4] px-6 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white shadow-lg shadow-[#2b46c4]/35 transition-all duration-200 hover:bg-[#2036a1] active:scale-95"
              >
                Localisation agences
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/souscription"
                data-testid="accueil-bandeau-souscription-bouton"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white px-7 py-3 text-[13px] font-bold uppercase tracking-wide text-white transition-all duration-200 hover:bg-white hover:text-[#101a33] active:scale-95"
              >
                Souscription en ligne
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </section>
        {/* Bande supplémentaire — les autres pays éligibles au service (liste du formulaire de Pré-enregistrement) */}
        <section className="overflow-hidden bg-white pt-14 pb-12 sm:pt-16 sm:pb-14" data-testid="accueil-pays-supplementaires">
          <div
            className="flex w-max animate-[defilement_40s_linear_infinite] hover:[animation-play-state:paused]"
            data-testid="accueil-pays-supplementaires-defilement"
          >
            {[false, true].map((clone) => (
              <div key={String(clone)} aria-hidden={clone || undefined} className="flex shrink-0 items-center">
                {PAYS_SUPPLEMENTAIRES.map((p) => (
                  <div
                    key={p.nom}
                    className="mx-7 flex w-36 shrink-0 flex-col items-center gap-2.5 sm:mx-10"
                    data-testid={clone ? undefined : `accueil-pays-supplementaires-${p.slug}`}
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white ring-2 ring-slate-100 shadow-[0_14px_28px_-10px_rgba(15,23,42,0.45),inset_0_2px_6px_rgba(255,255,255,0.7)] sm:h-[72px] sm:w-[72px]">
                      <img
                        src={p.image}
                        alt={`Drapeau : ${p.nomAffiche}`}
                        loading="lazy"
                        className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200/70 sm:h-[52px] sm:w-[52px]"
                      />
                    </span>
                    <p className="text-[13.5px] font-bold text-[#1e2f55]">{p.nomAffiche}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
